/**
 * Web Mercator (EPSG:3857) — de projectie van de KNMI-nowcast-WMS-frames én de
 * kaart-island. Eén bron van waarheid zodat de `/radar`-proxy (die de GetMap-bbox
 * berekent) en `site/islands/radar.ts` (die de kustlijn + pin in diezelfde bbox
 * projecteert) gegarandeerd uitlijnen.
 *
 * Sferische mercator met aard-straal 6378137 m (de EPSG:3857-conventie — geen
 * ellipsoïde-correctie; dat is precies wat WMS-tiles/adaguc gebruiken).
 * bron: https://epsg.io/3857
 */

/** Aardstraal in meters volgens de EPSG:3857-definitie. */
export const EARTH_RADIUS_M = 6378137;

const DEG2RAD = Math.PI / 180;

/** Lengtegraad (°) → x in EPSG:3857 (meters). */
export function lonToMercatorX(lon: number): number {
  return EARTH_RADIUS_M * lon * DEG2RAD;
}

/** Breedtegraad (°) → y in EPSG:3857 (meters). Klimt richting de polen. */
export function latToMercatorY(lat: number): number {
  return EARTH_RADIUS_M * Math.log(Math.tan(Math.PI / 4 + (lat * DEG2RAD) / 2));
}

/** lon/lat (°) → [x, y] in EPSG:3857 (meters). */
export function lonLatToMercator(lon: number, lat: number): [number, number] {
  return [lonToMercatorX(lon), latToMercatorY(lat)];
}

/** Een EPSG:3857-bbox `[minX, minY, maxX, maxY]` (meters) — de vaste radar-uitsnede. */
export type Bbox = readonly [number, number, number, number];

/**
 * Mercator-meter (x,y) → pixel binnen `bbox` op een w×h-vlak (canvas óf gedecodeerd
 * radar-PNG). y wordt omgekeerd: noord = boven. Gedeeld door de kaart-island en de
 * baker-nowcast, zodat dezelfde pin op dezelfde pixel valt.
 */
export function mercToPx(
  mx: number,
  my: number,
  bbox: Bbox,
  w: number,
  h: number,
): [number, number] {
  const [minX, minY, maxX, maxY] = bbox;
  return [((mx - minX) / (maxX - minX)) * w, ((maxY - my) / (maxY - minY)) * h];
}

/** lon/lat (°) → pixel binnen `bbox` op een w×h-vlak (via EPSG:3857). */
export function project(
  lon: number,
  lat: number,
  bbox: Bbox,
  w: number,
  h: number,
): [number, number] {
  return mercToPx(lonToMercatorX(lon), latToMercatorY(lat), bbox, w, h);
}

/**
 * De vaste uitsnede waarop de radar getoond wordt: heel NL gecentreerd, met een **brede
 * marge** west/oost (Belgische kust t/m het Duitse grensland) — verhouding ≈ 1,05 zodat het
 * kaartblad de kolombreedte vult (à la KNMI) en je buien al ziet aankomen vóór ze NL
 * raken. De radar is géén map-lib (§2): een vaste uitsnede, geen pan/zoom.
 */
export const NL_VIEW = { lonMin: 2.71, lonMax: 7.86, latMin: 50.65, latMax: 53.66 } as const;

/**
 * De NL-uitsnede als EPSG:3857-bbox `[minX, minY, maxX, maxY]` (meters). De WMS
 * GetMap wordt met precies deze bbox opgevraagd, zodat het radarbeeld de uitsnede
 * exact vult en de (her-geprojecteerde) kustlijn + pin er 1-op-1 op aansluiten.
 */
export function nlViewBbox3857(): [number, number, number, number] {
  return [
    lonToMercatorX(NL_VIEW.lonMin),
    latToMercatorY(NL_VIEW.latMin),
    lonToMercatorX(NL_VIEW.lonMax),
    latToMercatorY(NL_VIEW.latMax),
  ];
}
